import { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import type { IngestStatisticsResponse } from '@metron/shared';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toErrorMessage } from '@metron/shared';

const PROCESS_NAMES = {
  dailyBroadcast: { label: 'Daily Broadcast', color: '#3b82f6' },
  getLog: { label: 'Get Log', color: '#8b5cf6' },
  emergencyBroadcast: { label: 'Emergency Broadcast', color: '#f59e0b' },
  waveEmergencyBroadcast: { label: 'Wave Emergency Broadcast', color: '#f97316' },
  waveIngest: { label: 'Wave Ingest', color: '#10b981' },
  gatewayIngest: { label: 'Gateway Ingest', color: '#ec4899' },
  watersignalFlow: { label: 'Watersignal Flow', color: '#06b6d4' },
};

// Always show 24 hours of data at 5 minute resolution
const TIME_RANGE_MINUTES = 1440;
const INTERVAL_MINUTES = 5;

// Types for Recharts tooltip
interface TooltipPayload {
  dataKey: string;
  color: string;
  value: number;
  payload: Record<string, unknown>;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
}

// Custom tooltip component
function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const startTime = new Date(data.minute as string);
  const endTime = new Date(startTime.getTime() + INTERVAL_MINUTES * 60 * 1000);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='font-semibold mb-2'>
        {formatTime(startTime)} to {formatTime(endTime)}
      </p>
      <div className='space-y-1'>
        {payload.map((entry: TooltipPayload) => {
          const key = entry.dataKey;
          const isRejected = key.startsWith('rejected') || key === 'totalRejected';
          const isSuccess = key.startsWith('success');

          let label: string;
          let value: number;

          if (isRejected) {
            label = 'Rejected';
            value = data[key] as number;
          } else if (isSuccess) {
            // successEmergencyBroadcast -> emergencyBroadcast
            const processKey = key.charAt(7).toLowerCase() + key.slice(8);
            const totalKey = `total${key.slice(7)}`;
            value = (data[totalKey] as number) - (data[`rejected${key.slice(7)}`] as number ?? 0);
            const processInfo = PROCESS_NAMES[processKey as keyof typeof PROCESS_NAMES];
            label = processInfo?.label || processKey;
          } else {
            const totalKey = `total${key.charAt(0).toUpperCase()}${key.slice(1)}`;
            value = data[totalKey] as number;
            const processInfo = PROCESS_NAMES[key as keyof typeof PROCESS_NAMES];
            label = processInfo?.label || key;
          }

          if (value === 0 && isRejected) return null;

          return (
            <div key={key} className='flex items-center gap-2'>
              <div
                className='w-3 h-3 rounded-sm'
                style={{ backgroundColor: entry.color }}
              />
              <span className='text-sm'>
                {label}: <span className='font-semibold'>{value}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function IngestStatisticsPage() {
  const apiClient = useApiClient();
  const [data, setData] = useState<IngestStatisticsResponse['data']>([]);
  const [totals, setTotals] = useState<IngestStatisticsResponse['totals'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<IngestStatisticsResponse>(
          `ingest-statistics?timeRange=${TIME_RANGE_MINUTES}`,
        );
        // Reverse the data to show oldest first (for charts)
        setData(response.data.reverse());
        setTotals(response.totals);
      } catch (err) {
        setError(toErrorMessage(err, 'Failed to fetch ingest statistics'));
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [apiClient]);

  // Format data for charts — compute successful (total - rejected) per process for stacking
  const chartData = data.map((point) => {
    const date = new Date(point.minute);
    const totalRejected = point.rejectedGetLog + point.rejectedDailyBroadcast +
      point.rejectedWaveIngest + point.rejectedEmergencyBroadcast +
      point.rejectedWaveEmergencyBroadcast +
      point.rejectedGatewayIngest + point.rejectedWatersignalFlow;
    return {
      time: date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      ...point,
      totalRejected,
      // Successful = total - rejected for each process (for stacked individual charts)
      successGetLog: Math.max(0, point.getLog - point.rejectedGetLog),
      successDailyBroadcast: Math.max(0, point.dailyBroadcast - point.rejectedDailyBroadcast),
      successWaveIngest: Math.max(0, point.waveIngest - point.rejectedWaveIngest),
      successEmergencyBroadcast: Math.max(
        0,
        point.emergencyBroadcast - point.rejectedEmergencyBroadcast,
      ),
      successWaveEmergencyBroadcast: Math.max(
        0,
        point.waveEmergencyBroadcast - point.rejectedWaveEmergencyBroadcast,
      ),
      successGatewayIngest: Math.max(0, point.gatewayIngest - point.rejectedGatewayIngest),
      successWatersignalFlow: Math.max(0, point.watersignalFlow - point.rejectedWatersignalFlow),
    };
  });

  if (loading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Ingest Statistics</h1>
        <div className='flex items-center justify-center h-64'>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Ingest Statistics</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div className='flex items-center justify-between'>
        <h1 className='text-3xl font-bold'>Ingest Statistics</h1>
        <p className='text-muted-foreground'>Last 24 hours at 5-minute resolution</p>
      </div>

      {/* Overall Ingest - Stacked Area Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Ingest</CardTitle>
          <CardDescription>
            Stacked view of sensor updates across all ingest processes
            {totals && (
              <>
                {' • '}Total: {(
                  totals.getLog +
                  totals.dailyBroadcast +
                  totals.waveIngest +
                  totals.emergencyBroadcast +
                  totals.waveEmergencyBroadcast +
                  totals.gatewayIngest +
                  totals.watersignalFlow
                ).toLocaleString()} updates for {totals.distinctSensors.toLocaleString()}{' '}
                unique meters
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width='100%' height={400}>
            <AreaChart data={chartData}>
              <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
              <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={23} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {Object.entries(PROCESS_NAMES).map(([key, { label, color }]) => (
                <Area
                  key={key}
                  type='monotone'
                  dataKey={key}
                  stackId='1'
                  stroke={color}
                  fill={color}
                  name={label}
                />
              ))}
              <Line
                type='monotone'
                dataKey='totalRejected'
                stroke='#dc2626'
                strokeWidth={2}
                dot={false}
                name='Rejected (all)'
              />
            </AreaChart>
          </ResponsiveContainer>
          <p className='text-xs text-muted-foreground mt-4 mb-1'>Rejected Updates Detail</p>
          <ResponsiveContainer width='100%' height={120}>
            <AreaChart data={chartData}>
              <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
              <XAxis dataKey='time' hide />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type='monotone'
                dataKey='totalRejected'
                stroke='#dc2626'
                fill='#dc2626'
                fillOpacity={0.4}
                name='Rejected'
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Individual Process Charts - Area Charts */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {Object.entries(PROCESS_NAMES).map(([key, { label, color }]) => {
          const totalKey = key as keyof typeof PROCESS_NAMES;
          const total = totals?.[totalKey] ?? 0;
          const rejectedKey = `rejected${key.charAt(0).toUpperCase()}${
            key.slice(1)
          }` as keyof typeof totals;
          const rejectedTotal = totals?.[rejectedKey] ?? 0;
          const formattedTotal = total.toLocaleString();

          return (
            <Card key={key}>
              <CardHeader>
                <CardTitle className='text-lg'>{label}</CardTitle>
                <CardDescription>
                  Sensor updates • Total: {formattedTotal} updates
                  {(rejectedTotal as number) > 0 && (
                    <span className='text-red-500'>
                      &nbsp;• {(rejectedTotal as number).toLocaleString()}{' '}
                      rejected ({((rejectedTotal as number) / (total as number) * 100).toFixed(1)}%)
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width='100%' height={250}>
                  <AreaChart data={chartData}>
                    <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                    <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type='monotone'
                      dataKey={`rejected${key.charAt(0).toUpperCase()}${key.slice(1)}`}
                      stackId='process'
                      stroke='#dc2626'
                      fill='#dc2626'
                      fillOpacity={0.6}
                      name='Rejected'
                    />
                    <Area
                      type='monotone'
                      dataKey={`success${key.charAt(0).toUpperCase()}${key.slice(1)}`}
                      stackId='process'
                      stroke={color}
                      fill={color}
                      fillOpacity={0.6}
                      name={label}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
